import { useEffect, useRef, lazy, Suspense } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { useScrollStore } from '@/stores/scrollStore';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const PsychologyConstellation = lazy(() => import('./3d/PsychologyConstellation'));

gsap.registerPlugin(ScrollTrigger);

const researchers = [
  {
    name: 'Robert Cialdini',
    contribution: 'Principles of Persuasion',
    color: '#12263A',
  },
  {
    name: 'Daniel Kahneman',
    contribution: 'Cognitive Biases & Decision Making',
    color: '#5F6B7C',
  },
  {
    name: 'Amos Tversky',
    contribution: 'Prospect Theory & Loss Aversion',
    color: '#8A94A6',
  },
];

const principles = [
  'Scarcity',
  'Social Proof',
  'Anchoring',
  'Loss Aversion',
  'Reciprocity',
  'Authority',
  'Commitment',
  'Framing',
  'Bandwagon',
  'Curiosity Gap',
  'Peak-End Rule',
  'Status Quo Bias',
  'Mere Exposure',
  'Cognitive Ease',
  'Contrast Effect',
];

export function PsychologyEngineSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Pin section for 2x viewport scroll
      if (!isMobile) {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=200%',
          pin: true,
          scrub: 1,
          onUpdate: (self) => {
            useScrollStore.getState().setPsychProgress(self.progress);
          },
        });
      }

      // Text entrance
      gsap.from('.psych-content > *', {
        opacity: 0,
        y: 30,
        stagger: 0.1,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.psych-content',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });

      // Researcher cards stagger
      gsap.from('.researcher-card', {
        opacity: 0,
        y: 20,
        scale: 0.95,
        stagger: 0.12,
        duration: 0.6,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.researcher-cards',
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });

      // Principle nodes stagger (desktop 3D placeholder area)
      if (!isMobile) {
        gsap.from('.principle-node', {
          scale: 0,
          opacity: 0,
          stagger: 0.06,
          duration: 0.5,
          ease: 'back.out(1.5)',
          scrollTrigger: {
            trigger: '.principle-grid',
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  return (
    <section
      ref={sectionRef}
      id="psychology"
      className="relative min-h-screen overflow-hidden bg-paper"
      style={{
        paddingTop: 'clamp(80px, 10vh, 160px)',
        paddingBottom: 'clamp(80px, 10vh, 160px)',
      }}
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className={cn('flex gap-12', isMobile ? 'flex-col' : 'flex-row items-start')}>
          {/* Left side: text content */}
          <div className={cn('psych-content', isMobile ? 'w-full' : 'w-[35%] flex-shrink-0')}>
            <p
              className="mb-4 font-mono uppercase text-steel-dark"
              style={{
                fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
                fontWeight: 600,
                letterSpacing: '0.2em',
              }}
            >
              The Differentiator
            </p>

            <h2
              className="mb-6 text-ink font-display"
              style={{
                fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
                lineHeight: 1.1,
              }}
            >
              Powered by Psychology,
              <br />
              Not Just Prompts
            </h2>

            <p
              className="mb-10 max-w-[50ch] text-steel-dark font-body"
              style={{
                fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
                lineHeight: 1.65,
              }}
            >
              Every post ZUPA generates is engineered using 15+ research-backed psychological
              principles — Scarcity, Social Proof, Anchoring, Loss Aversion, and more. You see exactly which
              principles were applied and why they work.
            </p>

            {/* Researcher cards */}
            <div className="researcher-cards space-y-4">
              {researchers.map((r) => (
                <div
                  key={r.name}
                  className="researcher-card p-5 bg-white"
                  style={{
                    border: '1px solid #D8DCE3',
                    borderLeft: `3px solid ${r.color}`,
                  }}
                >
                  <h4
                    className="text-ink font-mono"
                    style={{
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    {r.name}
                  </h4>
                  <p
                    className="mt-1 text-steel-dark font-body"
                    style={{
                      fontSize: '1rem',
                    }}
                  >
                    {r.contribution}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right side: 3D constellation on desktop, principle nodes on mobile */}
          <div className={cn(isMobile ? 'w-full' : 'w-[65%] relative min-h-[850px]')}>
            {!isMobile ? (
              <Suspense fallback={<div className="w-full min-h-[600px]" />}>
                <PsychologyConstellation />
              </Suspense>
            ) : (
              <div
                className="principle-grid relative flex flex-wrap items-center justify-center gap-3"
              >
                {principles.map((p) => (
                  <motion.div
                    key={p}
                    className="principle-node"
                    whileHover={{
                      scale: 1.1,
                      borderColor: 'rgba(18, 38, 58, 0.4)',
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      className="cursor-pointer px-4 py-2 text-center transition-colors duration-200 bg-white"
                      style={{
                        border: '1px solid #D8DCE3',
                      }}
                    >
                      <span
                        className="text-ink/80 hover:text-cargo font-mono"
                        style={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        }}
                      >
                        {p}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
